import { Fragment } from "react"

import { TableCell, TableHead } from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  dataByModelPurpose,
  ModelPurpose,
} from "@/constants/terminal/model-purpose"
import { planByUsageProvider } from "@/constants/terminal/provider-rate"
import { UsageProvider } from "@/constants/terminal/usage-provider"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"
import type {
  PipelineUsage,
  UsageTotal,
} from "@/terminal/monitoring/monitoring-usage"
import { usageDollars } from "@/terminal/monitoring/usage-dollars"
import { usd } from "@/terminal/monitoring/usage-presentation"

// Stacking order bottom to top, and the one color each spend line wears on
// every admin surface. Slots 1–6 of the reference categorical palette,
// validated adjacent-pair on both Terminal surfaces; the light steps sit under
// 3:1, so every mark keeps a text value beside it or in its tooltip.
export const spendSeries = [
  { key: "llm", name: "LLM", light: "#2a78d6", dark: "#3987e5" },
  {
    key: UsageProvider.SERPAPI,
    name: planByUsageProvider[UsageProvider.SERPAPI].name,
    light: "#eb6834",
    dark: "#d95926",
  },
  {
    key: UsageProvider.SCRAPERAPI,
    name: planByUsageProvider[UsageProvider.SCRAPERAPI].name,
    light: "#1baf7a",
    dark: "#199e70",
  },
  {
    key: UsageProvider.BROWSERBASE,
    name: planByUsageProvider[UsageProvider.BROWSERBASE].name,
    light: "#eda100",
    dark: "#c98500",
  },
  {
    key: UsageProvider.APIFY,
    name: planByUsageProvider[UsageProvider.APIFY].name,
    light: "#e87ba4",
    dark: "#d55181",
  },
  {
    key: UsageProvider.PACER,
    name: planByUsageProvider[UsageProvider.PACER].name,
    light: "#008300",
    dark: "#008300",
  },
] as const
export type SpendSeriesKey = (typeof spendSeries)[number]["key"]

const untrackedProviderNames = new Intl.ListFormat("en-US").format(
  Object.values(planByUsageProvider).map(({ name }) => name)
)

export const formatQuantity = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
}).format

export const formatProviderUnits = (provider: UsageProvider, amount: number) =>
  `${formatQuantity(amount)} ${planByUsageProvider[provider].unit}`

// The header and stage strip count pending work in calls, so spend does too.
const formatRequests = (count: number) =>
  `${count} ${count === 1 ? "call" : "calls"}`

// Known plus estimated: what a chart or ranking can place.
export const placedAmount = (total: UsageTotal) => total.known + total.estimated

// Each spend line's dollars, a Run without provider tracking priced as LLM only.
export const spendBySeries = (usage: PipelineUsage) => {
  const dollars = usageDollars(usage)
  return {
    dollars,
    totalBySeries: {
      llm: usage.llm,
      ...dollars.byProvider,
    } as Record<SpendSeriesKey, UsageTotal>,
  }
}

export const SeriesSwatch = ({ seriesKey }: { seriesKey: SpendSeriesKey }) => {
  const { theme } = useTerminalTheme()
  const series = spendSeries.find(({ key }) => key === seriesKey)!
  return (
    <span
      aria-hidden
      className="inline-block size-2 shrink-0 rounded-xs"
      style={{ background: series[theme] }}
    />
  )
}

// A part-to-whole strip: each line's share of the dollars it sits beside.
const SpendMix = ({
  amountBySeries,
  className,
}: {
  amountBySeries: Record<SpendSeriesKey, number>
  className?: string
}) => {
  const { theme } = useTerminalTheme()
  const total = Object.values(amountBySeries).reduce(
    (sum, value) => sum + value
  )
  if (total <= 0) return <span className={cn("bg-border-faint", className)} />
  return (
    <span aria-hidden className={cn("flex gap-0.5", className)}>
      {spendSeries.map(
        (series) =>
          amountBySeries[series.key] > 0 && (
            <span
              key={series.key}
              className="h-full min-w-0.5 basis-0 first:rounded-l-xs last:rounded-r-xs"
              style={{
                background: series[theme],
                flexGrow: amountBySeries[series.key] / total,
              }}
            />
          )
      )}
    </span>
  )
}

// What a placed amount leaves out, or null when it is the whole story.
const partialNote = (
  total: UsageTotal,
  formatAmount: (value: number) => string
) => {
  const requests = [
    total.pendingRequests > 0 &&
      `${formatRequests(total.pendingRequests)} pending`,
    total.unknownRequests > 0 &&
      `${formatRequests(total.unknownRequests)} unknown`,
  ].filter(Boolean)
  if (total.estimated === 0 && requests.length === 0) return null
  return [
    `${formatAmount(total.known)} known`,
    total.estimated > 0 && ` + ${formatAmount(total.estimated)} estimated`,
    requests.length > 0 && `; ${requests.join("; ")}`,
  ]
    .filter(Boolean)
    .join("")
}

// Known plus estimated, with what that leaves out underneath.
const UsageAmount = ({
  total,
  isDollars = false,
}: {
  total: UsageTotal
  isDollars?: boolean
}) => {
  const formatAmount = isDollars ? usd : formatQuantity
  const note = partialNote(total, formatAmount)
  return (
    <>
      <span className="tabular-nums">{formatAmount(placedAmount(total))}</span>
      {note && (
        <span className="text-muted-foreground block text-xs">{note}</span>
      )}
    </>
  )
}

// One row per spend line: swatch, name, the units it was priced from, and its
// dollars; a partial line's note runs full width beneath it. Shared by the Run
// panel and the table-cell tooltip.
const SpendLines = ({ usage }: { usage: PipelineUsage }) => {
  const { totalBySeries } = spendBySeries(usage)
  const visibleSeries = usage.isProviderUsageTracked
    ? spendSeries
    : spendSeries.filter(({ key }) => key === "llm")
  return (
    <dl className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-4 gap-y-1">
      {visibleSeries.map((series) => {
        const note = partialNote(totalBySeries[series.key], usd)
        return (
          <Fragment key={series.key}>
            <dt className="flex items-center gap-2">
              <SeriesSwatch seriesKey={series.key} />
              {series.name}
            </dt>
            <dd className="text-muted-foreground text-right text-xs tabular-nums">
              {series.key !== "llm" &&
                formatProviderUnits(
                  series.key,
                  placedAmount(usage[series.key])
                )}
            </dd>
            <dd className="text-right tabular-nums">
              {usd(placedAmount(totalBySeries[series.key]))}
            </dd>
            {note && (
              <dd className="text-muted-foreground col-span-3 -mt-0.5 mb-1 pl-4 text-xs">
                {note}
              </dd>
            )}
          </Fragment>
        )
      })}
    </dl>
  )
}

const amountBySeriesOf = (usage: PipelineUsage) => {
  const { totalBySeries } = spendBySeries(usage)
  return Object.fromEntries(
    spendSeries.map(({ key }) => [key, placedAmount(totalBySeries[key])])
  ) as Record<SpendSeriesKey, number>
}

export const UsageColumnHeads = () => (
  <TableHead className="text-right">Spend</TableHead>
)

// A row's spend as one compact cell: the total, the provider mix, and the full
// breakdown on hover or focus. A Run that predates provider tracking shows its
// LLM total with no mix strip; the tooltip says why, instead of a "Not tracked"
// per provider.
export const UsageColumnCells = ({ usage }: { usage: PipelineUsage }) => {
  const { theme } = useTerminalTheme()
  const { dollars } = spendBySeries(usage)
  const unpricedRequests =
    dollars.total.pendingRequests + dollars.total.unknownRequests
  return (
    <TableCell className="text-right">
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="focus-visible:ring-terminal-accent inline-flex cursor-default flex-col items-end gap-1 rounded-xs outline-none focus-visible:ring-2"
            >
              <span className="font-semibold whitespace-nowrap tabular-nums">
                {usd(placedAmount(dollars.total))}
                {unpricedRequests > 0 && (
                  <span className="text-muted-foreground font-normal">+</span>
                )}
              </span>
              {usage.isProviderUsageTracked && (
                <SpendMix
                  amountBySeries={amountBySeriesOf(usage)}
                  className="h-1 w-16"
                />
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            align="center"
            collisionPadding={12}
            data-terminal-theme={theme}
            className={cn(
              "bg-popover border-border text-foreground shadow-tooltip w-80 rounded-sm border p-3 text-xs",
              theme === "dark" && "dark"
            )}
          >
            <SpendLines usage={usage} />
            <p className="border-border-faint mt-2 flex justify-between border-t pt-2 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">
                {usd(placedAmount(dollars.total))}
              </span>
            </p>
            {unpricedRequests > 0 && (
              <p className="text-muted-foreground mt-1">
                {formatRequests(unpricedRequests)} not yet priced, so the total
                is a floor.
              </p>
            )}
            {!usage.isProviderUsageTracked && (
              <p className="text-muted-foreground mt-2">
                {untrackedProviderNames} weren’t tracked for this Run.
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </TableCell>
  )
}

export const UsageView = ({ usage }: { usage: PipelineUsage }) => {
  const { dollars } = spendBySeries(usage)
  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-2">
        <p className="font-display text-2xl leading-none font-black tabular-nums">
          {usd(placedAmount(dollars.total))}
        </p>
        <p className="text-muted-foreground text-xs">
          {partialNote(dollars.total, usd) ?? "Total dollars"}
        </p>
        {usage.isProviderUsageTracked && (
          <SpendMix
            amountBySeries={amountBySeriesOf(usage)}
            className="h-1.5 w-full max-w-sm"
          />
        )}
      </div>
      {usage.isProviderUsageTracked && (
        <div className="max-w-sm">
          <SpendLines usage={usage} />
        </div>
      )}
      {!usage.isProviderUsageTracked && (
        <p className="text-muted-foreground text-xs">
          This Run predates provider tracking: {untrackedProviderNames} usage
          wasn’t tracked here, so the total is LLM dollars only.
        </p>
      )}
      {Object.keys(usage.llmByPurpose).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer">LLM dollars by purpose</summary>
          <dl className="mt-2 grid max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1.5">
            {Object.values(ModelPurpose).map((purpose) => {
              const total = usage.llmByPurpose[purpose]
              return (
                total && (
                  <Fragment key={purpose}>
                    <dt className="text-muted-foreground">
                      {dataByModelPurpose[purpose].label}
                    </dt>
                    <dd className="text-right">
                      <UsageAmount total={total} isDollars />
                    </dd>
                  </Fragment>
                )
              )
            })}
          </dl>
        </details>
      )}
    </div>
  )
}
